import { useEffect, useState } from "react";
import { userData, focusElement } from "../js/utils";
import { post } from "../js/post";
import InputData from "./InputData";
import InputIconSearchPanelMultiSelection from "./InputIconSearchPanelMultiSelection";
import InputIconSearchPanelCustomSelection from "./InputIconSearchPanelCustomSelection";
import Tag from "./Tag";
import CheckBox from "./CheckBox";
import { useOptics } from "../hooks/useOptics";
import { useAmetropias } from "../hooks/useAmetropias";
import { useHobbies } from "../hooks/useHobbies";
import { useProfessions } from "../hooks/useProfessions";
import { useSystemProvinces } from "../hooks/useSystemProvinces";

export function DashboardPatientPanelFilterSection({
  onChange,
  status = null,
  onClick = null,
}) {
  const opticalList = useOptics();
  const [selectedOpticalList, setSelectedOpticalList] = useState([]);
  const [locationList, setLocationList] = useState([]);
  const [selectedLocationList, setSelectedLocationList] = useState([]);
  const provinceList = useSystemProvinces();
  const [selectedProvinceList, setSelectedProvinceList] = useState([]);
  const [postalCodeList, setPostalCodeList] = useState([]);
  const [selectedPostalCodeList, setSelectedPostalCodeList] = useState([]);
  const ametropiaList = useAmetropias();
  const [selectedAmetropiaList, setSelectedAmetropiaList] = useState([]);
  const hobbiesList = useHobbies();
  const [selectedHobbiesList, setSelectedHobbiesList] = useState([]);
  const professionsList = useProfessions();
  const [selectedProfessionsList, setSelectedProfessionsList] = useState([]);
  const [selectedUserOptionsList, setSelectedUserOptionsList] = useState([]);

  const [toggleData, setToggleData] = useState({
    male: false,
    female: false,
    hasChildren: false,
    hasDriverLicense: false,
  });
  const [age, setAge] = useState({ from: "", to: "" });
  const [dates, setDates] = useState({
    registrationDateFrom: "",
    registrationDateTo: "",
    lastVisitFrom: "",
    lastVisitTo: "",
  });

  const getLocations = async () => {
    const response = await post(
      "support-extra/dashboard/ListarLocalidades.php",
      {
        token: userData.token,
      },
    );
    setLocationList([...response.map((e) => ({ id: e, text: e }))]);
  };

  const getPostalCodes = async () => {
    const response = await post(
      "support-extra/dashboard/ListarCodigosPostales.php",
      {
        token: userData.token,
      },
    );
    setPostalCodeList([...response.map((e) => ({ id: e, text: e }))]);
  };

  function resetFilters() {
    setSelectedOpticalList([]);
    setSelectedLocationList([]);
    setSelectedProvinceList([]);
    setSelectedPostalCodeList([]);
    setSelectedUserOptionsList([]);
    setSelectedAmetropiaList([]);
    setSelectedHobbiesList([]);
    setSelectedProfessionsList([]);
    setAge({ from: "", to: "" });
    setToggleData({
      male: false,
      female: false,
      hasChildren: false,
      hasDriverLicense: false,
    });
    setDates({
      registrationDateFrom: "",
      registrationDateTo: "",
      lastVisitFrom: "",
      lastVisitTo: "",
    });
  }

  useEffect(() => {
    getLocations();
    getPostalCodes();
  }, []);

  useEffect(() => {
    onChange({
      opticasAlta: opticalList
        .filter((e) => selectedOpticalList.includes(e.text))
        .map((e) => e.id),
      localidades: selectedLocationList,
      provincias: selectedProvinceList,
      codigosPostales: selectedPostalCodeList,
      edadDesde: age.from,
      edadHasta: age.to,
      hombre: toggleData.male,
      mujer: toggleData.female,
      tieneHijos: toggleData.hasChildren,
      tieneCarnet: toggleData.hasDriverLicense,
      usuariosDe: selectedUserOptionsList.map((e) => e.id),
      ametropias: selectedAmetropiaList,
      aficiones: selectedHobbiesList,
      profesiones: selectedProfessionsList,
      fechaAltaDesde: dates.registrationDateFrom,
      fechaAltaHasta: dates.registrationDateTo,
      ultimaVisitaDesde: dates.lastVisitFrom,
      ultimaVisitaHasta: dates.lastVisitTo,
    });
  }, [
    selectedOpticalList,
    selectedLocationList,
    selectedProvinceList,
    selectedPostalCodeList,
    selectedUserOptionsList,
    selectedAmetropiaList,
    selectedHobbiesList,
    selectedProfessionsList,
    age,
    toggleData,
    dates,
  ]);

  return (
    <div className="dashboard-section">
      <div className="dashboard-section-title">
        <div
          className="opt-table-title opt-table-title-dash-stats"
          style={{ justifyContent: "flex-start" }}
        >
          Filtros generales
          {status !== null && (
            <button className="cell-icon" onClick={onClick}>
              <img
                src={
                  status
                    ? "/images/arrow-up-blue.svg"
                    : "/images/arrow-down-blue.svg"
                }
                loading="lazy"
                alt="Icon"
                className="cell-icon"
              ></img>
            </button>
          )}
        </div>
        <button
          className="cell-label"
          tabIndex={-1}
          style={{ width: "120px" }}
          onClick={resetFilters}
        >
          Limpiar filtros
        </button>
      </div>
      <div>
        <div className="dashboard-sec-filters">
          <div className="store-col-sec" style={{ width: "50%" }}>
            <div className="data-field">
              <span className="label">Óptica de alta</span>
              <InputIconSearchPanelMultiSelection
                size={0}
                tabIndex={30}
                type={"type-alphanum-ext"}
                options={opticalList.map((e) => e.text)}
                selectedOptions={selectedOpticalList}
                onChange={(e) => {
                  setSelectedOpticalList(e);
                }}
              ></InputIconSearchPanelMultiSelection>
            </div>
            {selectedOpticalList.length > 0 && (
              <span style={{ fontWeight: "500", fontSize: 13 }}>
                Ópticas seleccionadas: {selectedOpticalList.length}
              </span>
            )}
            <div className="tag-content">
              {selectedOpticalList.map((e, i) => {
                return (
                  <Tag
                    text={e}
                    key={i}
                    tagId={i}
                    onClick={(e) => {
                      setSelectedOpticalList((prev) =>
                        prev.filter((element) => element !== e),
                      );
                    }}
                  ></Tag>
                );
              })}
            </div>
            <div className="data-field">
              <span className="label">Localidad</span>
              <InputIconSearchPanelMultiSelection
                size={0}
                tabIndex={31}
                type={"type-alphanum-ext"}
                options={locationList.map((e) => e.text)}
                selectedOptions={selectedLocationList}
                onChange={(e) => {
                  setSelectedLocationList(e);
                }}
              ></InputIconSearchPanelMultiSelection>
            </div>
            {selectedLocationList.length > 0 && (
              <span style={{ fontWeight: "500", fontSize: 13 }}>
                Localidades seleccionadas: {selectedLocationList.length}
              </span>
            )}
            <div className="tag-content">
              {selectedLocationList.map((e, i) => {
                return (
                  <Tag
                    text={e}
                    key={i}
                    tagId={i}
                    onClick={(e) => {
                      setSelectedLocationList((prev) =>
                        prev.filter((element) => element !== e),
                      );
                    }}
                  ></Tag>
                );
              })}
            </div>
            <div className="data-field">
              <span className="label">Provincia</span>
              <InputIconSearchPanelMultiSelection
                size={0}
                tabIndex={32}
                type={"type-alphanum-ext"}
                options={provinceList
                  .filter((e) => e.id !== "")
                  .map((e) => e.text)}
                selectedOptions={selectedProvinceList}
                onChange={(e) => {
                  setSelectedProvinceList(e);
                }}
              ></InputIconSearchPanelMultiSelection>
            </div>
            {selectedProvinceList.length > 0 && (
              <span style={{ fontWeight: "500", fontSize: 13 }}>
                Provincias seleccionadas: {selectedProvinceList.length}
              </span>
            )}
            <div className="tag-content">
              {selectedProvinceList.map((e, i) => {
                return (
                  <Tag
                    text={e}
                    key={i}
                    tagId={i}
                    onClick={(e) => {
                      setSelectedProvinceList((prev) =>
                        prev.filter((element) => element !== e),
                      );
                    }}
                  ></Tag>
                );
              })}
            </div>
            <div className="data-field">
              <span className="label">C. Postal</span>
              <InputIconSearchPanelMultiSelection
                size={0}
                tabIndex={33}
                type={"type-alphanum-ext"}
                options={postalCodeList.map((e) => e.text)}
                selectedOptions={selectedPostalCodeList}
                onChange={(e) => {
                  setSelectedPostalCodeList(e);
                }}
              ></InputIconSearchPanelMultiSelection>
            </div>
            {selectedPostalCodeList.length > 0 && (
              <span style={{ fontWeight: "500", fontSize: 13 }}>
                Códigos seleccionados: {selectedPostalCodeList.length}
              </span>
            )}
            <div className="tag-content">
              {selectedPostalCodeList.map((e, i) => {
                return (
                  <Tag
                    text={e}
                    key={i}
                    tagId={i}
                    onClick={(e) => {
                      setSelectedPostalCodeList((prev) =>
                        prev.filter((element) => element !== e),
                      );
                    }}
                  ></Tag>
                );
              })}
            </div>
          </div>
          <div className="store-col-sec" style={{ width: "50%" }}>
            <div className="data-field">
              <span className="label">Usuario de</span>
              <InputIconSearchPanelCustomSelection
                size={0}
                tabIndex={34}
                type={"type-alphanum-ext"}
                selectedOptions={selectedUserOptionsList}
                onChange={(e) => {
                  setSelectedUserOptionsList(e);
                }}
              ></InputIconSearchPanelCustomSelection>
            </div>
            {selectedUserOptionsList.length > 0 && (
              <span style={{ fontWeight: "500", fontSize: 13 }}>
                Opciones seleccionadas: {selectedUserOptionsList.length}
              </span>
            )}
            <div className="tag-content">
              {selectedUserOptionsList.map((e) => {
                return (
                  <Tag
                    text={e.text}
                    key={e.id}
                    tagId={e.id}
                    onClick={() => {
                      setSelectedUserOptionsList((prev) =>
                        prev.filter((element) => element.id !== e.id),
                      );
                    }}
                  ></Tag>
                );
              })}
            </div>
            <div className="data-field">
              <span className="label">Ametropías</span>
              <InputIconSearchPanelMultiSelection
                size={0}
                tabIndex={35}
                type={"type-alphanum-ext"}
                options={ametropiaList.map((e) => e.text)}
                selectedOptions={selectedAmetropiaList}
                onChange={(e) => {
                  setSelectedAmetropiaList(e);
                }}
              ></InputIconSearchPanelMultiSelection>
            </div>
            {selectedAmetropiaList.length > 0 && (
              <span style={{ fontWeight: "500", fontSize: 13 }}>
                Ametropías seleccionadas: {selectedAmetropiaList.length}
              </span>
            )}
            <div className="tag-content">
              {selectedAmetropiaList.map((e, i) => {
                return (
                  <Tag
                    text={e}
                    key={i}
                    tagId={i}
                    onClick={(e) => {
                      setSelectedAmetropiaList((prev) =>
                        prev.filter((element) => element !== e),
                      );
                    }}
                  ></Tag>
                );
              })}
            </div>
            <div className="data-field">
              <span className="label">Aficiones</span>
              <InputIconSearchPanelMultiSelection
                size={0}
                tabIndex={36}
                type={"type-alphanum-ext"}
                options={hobbiesList.map((e) => e.text)}
                selectedOptions={selectedHobbiesList}
                onChange={(e) => {
                  setSelectedHobbiesList(e);
                }}
              ></InputIconSearchPanelMultiSelection>
            </div>
            {selectedHobbiesList.length > 0 && (
              <span style={{ fontWeight: "500", fontSize: 13 }}>
                Aficiones seleccionadas: {selectedHobbiesList.length}
              </span>
            )}
            <div className="tag-content">
              {selectedHobbiesList.map((e, i) => {
                return (
                  <Tag
                    text={e}
                    key={i}
                    tagId={i}
                    onClick={(e) => {
                      setSelectedHobbiesList((prev) =>
                        prev.filter((element) => element !== e),
                      );
                    }}
                  ></Tag>
                );
              })}
            </div>
            <div className="data-field">
              <span className="label">Profesión</span>
              <InputIconSearchPanelMultiSelection
                size={0}
                tabIndex={37}
                type={"type-alphanum-ext"}
                options={professionsList.map((e) => e.text)}
                selectedOptions={selectedProfessionsList}
                onChange={(e) => {
                  setSelectedProfessionsList(e);
                }}
              ></InputIconSearchPanelMultiSelection>
            </div>
            {selectedProfessionsList.length > 0 && (
              <span style={{ fontWeight: "500", fontSize: 13 }}>
                Profesiones seleccionadas: {selectedProfessionsList.length}
              </span>
            )}
            <div className="tag-content">
              {selectedProfessionsList.map((e, i) => {
                return (
                  <Tag
                    text={e}
                    key={i}
                    tagId={i}
                    onClick={(e) => {
                      setSelectedProfessionsList((prev) =>
                        prev.filter((element) => element !== e),
                      );
                    }}
                  ></Tag>
                );
              })}
            </div>
          </div>
        </div>
      </div>
      <div className="opt-table-content grid-14" id="patient-general-filters">
        <div className="cell cell-start-content">Hombre</div>
        <div className="cell cell-start-content">
          <CheckBox
            tabIndex={38}
            active={toggleData.male}
            onClick={() => {
              setToggleData((prev) => ({ ...prev, male: !prev.male }));
            }}
          ></CheckBox>
        </div>
        <div className="cell"></div>
        <div className="cell cell-start-content">Tiene hijos</div>
        <div className="cell cell-start-content">
          <CheckBox
            tabIndex={40}
            active={toggleData.hasChildren}
            onClick={() => {
              setToggleData((prev) => ({
                ...prev,
                hasChildren: !prev.hasChildren,
              }));
            }}
          ></CheckBox>
        </div>
        <div className="cell"></div>
        <div className="cell cell-start-content">Edad desde</div>
        <div className="cell cell-start-content">
          <InputData
            type="type-integer"
            size={0}
            tabIndex={42}
            input={age.from}
            onChange={(e) => {
              setAge((prev) => ({ ...prev, from: e }));
            }}
          ></InputData>
        </div>
        <div className="cell"></div>
        <div className="cell cell-start-content">Fecha alta desde</div>
        <div className="cell cell-start-content">
          <InputData
            type="date"
            max="9999-12-31"
            size={0}
            tabIndex={44}
            input={dates.registrationDateFrom}
            onChange={(e) => {
              setDates((prev) => ({
                ...prev,
                registrationDateFrom: e,
              }));
            }}
          ></InputData>
        </div>
        <div className="cell"></div>
        <div className="cell">Última visita desde</div>
        <div className="cell">
          <InputData
            type="date"
            max="9999-12-31"
            size={0}
            tabIndex={46}
            input={dates.lastVisitFrom}
            onChange={(e) => {
              setDates((prev) => ({
                ...prev,
                lastVisitFrom: e,
              }));
            }}
          ></InputData>
        </div>

        <div className="cell cell-start-content">Mujer</div>
        <div className="cell cell-start-content">
          <CheckBox
            tabIndex={39}
            active={toggleData.female}
            onClick={() => {
              setToggleData((prev) => ({ ...prev, female: !prev.female }));
            }}
          ></CheckBox>
        </div>
        <div className="cell"></div>
        <div className="cell cell-start-content">Tiene carné</div>
        <div className="cell cell-start-content">
          <CheckBox
            tabIndex={41}
            active={toggleData.hasDriverLicense}
            onClick={() => {
              setToggleData((prev) => ({
                ...prev,
                hasDriverLicense: !prev.hasDriverLicense,
              }));
            }}
          ></CheckBox>
        </div>
        <div className="cell"></div>
        <div className="cell cell-start-content">Edad hasta</div>
        <div className="cell cell-start-content">
          <InputData
            type="type-integer"
            size={0}
            tabIndex={43}
            input={age.to}
            onChange={(e) => {
              setAge((prev) => ({ ...prev, to: e }));
            }}
          ></InputData>
        </div>
        <div className="cell"></div>
        <div className="cell cell-start-content">Fecha alta hasta</div>
        <div className="cell cell-start-content">
          <InputData
            type="date"
            max="9999-12-31"
            size={0}
            tabIndex={45}
            input={dates.registrationDateTo}
            onChange={(e) => {
              setDates((prev) => ({
                ...prev,
                registrationDateTo: e,
              }));
            }}
          ></InputData>
        </div>
        <div className="cell"></div>
        <div className="cell">Última visita hasta</div>
        <div className="cell">
          <InputData
            type="date"
            max="9999-12-31"
            size={0}
            tabIndex={47}
            input={dates.lastVisitTo}
            onChange={(e) => {
              setDates((prev) => ({
                ...prev,
                lastVisitTo: e,
              }));
            }}
          ></InputData>
        </div>
      </div>
    </div>
  );
}
